import React, { useMemo, useState } from 'react';
import { Card, Select, Space, Typography, Alert, Modal } from 'antd';
import modalRegistry from '../../components/modals';
import Button from '../../components/common/Button';

// A lightweight harness page to pick any modal from src/components/modals and open it
// Assumes each modal supports the common AntD props: open (or visible), onOk, onCancel, and title
// If some modals need extra props, we can extend this page later with per-modal adapters.

const { Title, Text } = Typography;

type RegistryItem = {
  name: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  component: React.ComponentType<any>;
};

const CormodalsDisplay: React.FC = () => {
  const [selectedName, setSelectedName] = useState<string | undefined>();
  const [open, setOpen] = useState(false);
  // Store last payload returned by a modal's onOk for quick verification
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [lastResult, setLastResult] = useState<any>(null);
  const [resultModalOpen, setResultModalOpen] = useState(false);

  const items: RegistryItem[] = useMemo(() => modalRegistry as unknown as RegistryItem[], []);
  const selected = useMemo(() => items.find(i => i.name === selectedName), [items, selectedName]);

  const handleOpen = () => {
    if (!selectedName) return;
    setOpen(true);
  };

  const handleCancel = () => setOpen(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleOk = (result?: any) => {
    setLastResult(result ?? null);
    setOpen(false);
    setResultModalOpen(true);
  };

  const SelectedModal = selected?.component as RegistryItem['component'] | undefined;

  return (
    <Card style={{ margin: 16 }}>
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <Title level={4}>Core Modals Display</Title>
        <Text>Select a modal from the list and click "Open" to test it.</Text>

        <Space wrap>
          <Select
            showSearch
            placeholder="Select a modal"
            style={{ minWidth: 280 }}
            value={selectedName}
            onChange={(v) => setSelectedName(v)}
            options={items.map(i => ({ label: i.name, value: i.name }))}
            optionFilterProp="label"
          />
          <Button type="primary" onClick={handleOpen} disabled={!selectedName}>Open</Button>
        </Space>

        {SelectedModal ? (
          // Render the selected modal with common props. Pass both open and visible for compatibility.
          <SelectedModal
            open={open}
            visible={open}
            onCancel={handleCancel}
            onOk={handleOk}
            title={selectedName}
            {...(selectedName === 'PersonnelSelect' ? { returnAllModes: true } : {})}
            {...(selectedName === 'WeldSelectModal' ? { returnAllModes: true } : {})}
            {...(selectedName === 'FolderFileSearchModal' ? { returnAllModes: true } : {})}
          />
        ) : (
          <Alert type="info" message="No modal selected" showIcon />
        )}

        {/* Integrated result viewer modal */}
        <Modal
          open={resultModalOpen}
          onCancel={() => setResultModalOpen(false)}
          onOk={() => setResultModalOpen(false)}
          title="Selection Result"
          width={800}
        >
          <Typography.Paragraph type="secondary">
            Below is the payload returned by the selected modal's onOk.
          </Typography.Paragraph>
          <pre style={{ background: '#f6f8fa', padding: 12, borderRadius: 6, maxHeight: 420, overflow: 'auto' }}>
{typeof lastResult === 'string' ? lastResult : JSON.stringify(lastResult, null, 2)}
          </pre>
        </Modal>

      </Space>
    </Card>
  );
};

export default CormodalsDisplay;
